import { useContext, useMemo } from 'react'
import isBookingProtectionEnabled from 'checkout/selectors/request/isBookingProtectionEnabled'
import { isInsuranceBookingProtectionEnabledForCurrentRegion } from 'checkout/selectors/request/isBookingProtectionAvailable'
import { selectShouldUseInsuranceMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { Experiments } from 'constants/experiments'
import StandaloneFlightStateContext from 'contexts/Flights/StandaloneFlights/standaloneFlightsStateContext'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFlightsWithoutFareFamilyInsuranceQuote, { QUOTE_STATUS } from 'hooks/Insurance/useFlightsWithoutFareFamilyInsuranceQuote'
import { useAppSelector } from 'hooks/reduxHooks'
import { countOccupantsFromPassengers } from 'lib/flights/countOccupantUtils'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'
import { buildSyntheticDisplayFareFamilies } from 'lib/flights/syntheticFareFamilyUpsell'
import { getFlightSearchInsuranceQuoteKey, isStandaloneMaldivesCharterFlight } from 'lib/insurance/insuranceFlights'
import { shouldFetchInsuranceQuotes } from 'lib/insurance/insuranceUtils'
import { getIsEnabledFlightsBundledLuxPlusPricing, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'

interface Props {
  enabled: boolean
  journeys: Array<App.JourneyV2>
  occupancies?: App.Occupants
  isStandalone: boolean
  isMultiCity?: boolean
  isBundledFlow?: boolean
  journeyPrice: number
  departingDate?: string
  arrivalDate?: string
  destinationCountries?: Array<string>
  provider?: App.FlightProviders
  originAirportCode?: string
  destinationAirportCode?: string
}

const emptyCachedFareFamilies: App.FlightsState['fareFamiliesByFlight'][0] = {
  families: [],
  familiesPerLeg: [],
  fetching: true,
  error: undefined,
}

function useSyntheticFareFamilyUpsell({
  enabled,
  journeys,
  occupancies: occupanciesProp,
  isStandalone,
  isMultiCity = false,
  isBundledFlow = !isStandalone,
  journeyPrice,
  departingDate,
  arrivalDate,
  destinationCountries,
  provider,
  originAirportCode,
  destinationAirportCode,
}: Props) {
  const { passengers } = useContext(StandaloneFlightStateContext)
  const occupancies = useMemo(
    () => occupanciesProp ?? countOccupantsFromPassengers(passengers),
    [occupanciesProp, passengers],
  )
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const bookingProtectionEnabled = useAppSelector(state => isBookingProtectionEnabled(state))
  const isBookingProtectionRegion = useAppSelector(isInsuranceBookingProtectionEnabledForCurrentRegion)
  const useMemberInsurancePrice = useAppSelector(selectShouldUseInsuranceMemberPrice)
  const isFlightLuxPlusPricingEligible = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: isBundledFlow ? getIsEnabledFlightsBundledLuxPlusPricing(state) : getIsEnabledFlightsLuxPlusPricing(state),
    isBundledFlow,
  }))
  const shouldHideEmbeddedInsurance = isStandaloneMaldivesCharterFlight({
    isStandalone,
    provider,
    originAirportCode,
    destinationAirportCode,
  })
  const canTriggerBundledFlexibleFlightFaresExperiment = enabled && !headlessMode && !isStandalone && bookingProtectionEnabled
  const { isAnyVariant: isCroBundledFlexibleFlightFaresVariant } = useExperiment(
    Experiments.croBundledFlexibleFlightFaresV2,
    { canActivate: canTriggerBundledFlexibleFlightFaresExperiment },
  )
  const isCroBundledFlexibleFlightFaresEnabled = canTriggerBundledFlexibleFlightFaresExperiment && isCroBundledFlexibleFlightFaresVariant
  const isFetchingInsuranceQuoteEnabled = enabled &&
    !isMultiCity &&
    !shouldHideEmbeddedInsurance &&
    shouldFetchInsuranceQuotes({
      isStandalone,
      isCroBundledFlexibleFlightFaresEnabled,
      bookingProtectionEnabled,
      isBookingProtectionRegion,
    })

  const { quote, quoteStatus } = useFlightsWithoutFareFamilyInsuranceQuote({
    journeyPrice,
    departingDate,
    arrivalDate,
    destinationCountries,
    occupants: occupancies,
    options: {
      disabled: !isFetchingInsuranceQuoteEnabled,
    },
  })

  const fetching = enabled && isFetchingInsuranceQuoteEnabled && quoteStatus === QUOTE_STATUS.FETCHING
  const protectQuote = quoteStatus === QUOTE_STATUS.AVAILABLE ? quote : undefined

  const families = useMemo(() => {
    if (!enabled || fetching) return []

    return buildSyntheticDisplayFareFamilies({
      journeys,
      quote: protectQuote,
      occupancies,
      useMemberInsurancePrice,
      isFlightLuxPlusPricingEligible,
    })
  }, [
    enabled,
    fetching,
    isFlightLuxPlusPricingEligible,
    journeys,
    occupancies,
    protectQuote,
    useMemberInsurancePrice,
  ])

  const cachedFareFamilies = useMemo<App.FlightsState['fareFamiliesByFlight'][0]>(() => {
    if (!enabled) return emptyCachedFareFamilies

    return {
      families,
      familiesPerLeg: [],
      fetching,
      error: undefined,
    }
  }, [enabled, families, fetching])

  const insuranceQuotes = useMemo(() => {
    if (!protectQuote) return {}

    const quoteKey = getFlightSearchInsuranceQuoteKey(
      departingDate,
      arrivalDate,
      journeyPrice,
      destinationCountries?.[0],
      occupancies,
    )

    return { [quoteKey]: protectQuote }
  }, [arrivalDate, departingDate, destinationCountries, journeyPrice, occupancies, protectQuote])

  return {
    families,
    fetching,
    cachedFareFamilies,
    insuranceQuotes,
    quote: protectQuote,
  }
}

export default useSyntheticFareFamilyUpsell
